import { useCallback, useRef, useState, useEffect } from 'react';
import BigButton from '../../button/BigButton';
import PCode from './input/PCode';
import PName from './input/PName';
import PCost from './input/PCost';
import PService from './input/PService';
import PDescription from './input/PDescription';
import PImage from './input/PImage';
import PThumbnail from './input/PThumbnail';
import PInlineImage from './input/PInlineImage';
import { toast } from 'react-toastify';
import {categories, PostProductRequest, useProductStore} from '../../../store/useProduct';
import { useNavigate } from 'react-router-dom';
import PDiscount from './input/PDiscount';
import Input from "../../form/Input";
import apiClient from '../../../service/apiClient';

export interface PRef<T extends HTMLElement = HTMLInputElement> {
  pRef: React.RefObject<T>;
}

/**
 * @author taulmax
 * @date 2024.10.09
 * @description 상품 등록 페이지
 */
const ProductCreate = () => {
  const [pCost, setPCost] = useState<string>('');
  const [pName, setPName] = useState<string>('');
  const [pDiscount, setPDiscount] = useState<string>('');

  const pCodeRef = useRef<HTMLInputElement>(null);
  const pNameRef = useRef<HTMLInputElement>(null);
  const pCostRef = useRef<HTMLInputElement>(null);
  const pDiscountRef = useRef<HTMLInputElement>(null);
  const pDescriptionRef = useRef<HTMLTextAreaElement>(null);
  const pServiceRef = useRef<HTMLSelectElement>(null);

  const [endFiles, setEndFiles] = useState<File[]>([]);
  const [newEndImage, setNewEndImage] = useState<{file: File; id: string} | null>(null);
  const [existingEnd, setExistingEnd] = useState<string>('');

  const [pThumbnailId, setPThumbnailId] = useState<{
    file: File;
    id: string;
  } | null>(null);
  const [pImagesId, setPImagesId] = useState<{ file: File; id: string }[]>([]);

  const [pInlineId, setPInlineId] = useState<{
    file: File;
    id: string;
  } | null>(null);

  const [pEndFileId, setPEndFileId] = useState<{
    file: File;
    id: string;
  } | null>(null);

  const { postProduct, searchCategories } = useProductStore();

  const navigate = useNavigate();

  const [categoryName, setCategoryName] = useState<string>('');
  const [categoryList, setCategoryList] = useState<categories[]>([]);
  const [showCategoryList, setShowCategoryList] = useState<boolean>(false);
  const categoryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [newSampleFile, setNewSampleFile] = useState<{file: File; id: string} | null>(null);
  const [existingSampleFile, setExistingSampleFile] = useState<string>('');
  const [sampleFiles, setSampleFiles] = useState<File[]>([]);

  // 등록 버튼 클릭
  const onClickSubmit = useCallback(() => {
    if (!pCodeRef.current?.value) {
      toast.error('상품코드를 입력해주세요.');
      return;
    }
    if (!pNameRef.current?.value) {
      toast.error('상품명을 입력해주세요.');
      return;
    }
    if (!pCostRef.current?.value) {
      toast.error('금액을 입력해주세요.');
      return;
    }
    if (!pDescriptionRef.current?.value) {
      toast.error('간단 설명을 입력해주세요.');
      return;
    }
    if (!pServiceRef.current?.value) {
      toast.error('서비스 형태를 입력해주세요.');
      return;
    }
    if (!pThumbnailId) {
      toast.error('썸네일(대표사진)을 업로드해주세요.');
      return;
    }
    if (!pInlineId) {
      toast.error('속지 이미지를 업로드해주세요.');
      return;
    }
    if (pImagesId.length === 0) {
      toast.error('상세 이미지를 업로드해주세요.');
      return;
    }

    const productData: PostProductRequest = {
      productCode: pCodeRef.current?.value,
      productName: pNameRef.current?.value,
      custInfoCount: parseInt(pServiceRef.current?.value) as 1 | 2,
      price: parseInt(pCostRef.current?.value.replaceAll(',', '')),
      description: pDescriptionRef.current?.value,
      thumbnailFileId: pThumbnailId.id,
      inlineFileId: pInlineId.id,
      ...(pEndFileId && { endFileId: pEndFileId.id }),
      detailFileIds: pImagesId.map(item => item.id),
      categoryName: categoryName,
      ...(newSampleFile && { sampleFileId: newSampleFile.id })
    };

    if (pDiscountRef.current && pDiscountRef.current.value) {
      productData.discountPrice = parseInt(
        pDiscountRef.current?.value.replaceAll(',', '')
      );
    }

    postProduct(productData, navigate);
  }, [navigate, pImagesId, pThumbnailId, pInlineId, pEndFileId, postProduct, categoryName, newSampleFile]);

  // 샘플파일 업로드 처리 (PDF)
  useEffect(() => {
    const uploadSampleFile = async () => {
      if (sampleFiles.length > 0) {
        const file = sampleFiles[0];

        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await apiClient.post('/upload-file/upload', formData);
          setNewSampleFile({ file, id: response.data.id });
          setSampleFiles([]); // 업로드 후 초기화
        } catch (error) {
          console.error('파일 업로드 실패:', error);
          toast.error('파일 업로드를 실패했습니다.');
        }
      }
    };

    uploadSampleFile();
  }, [sampleFiles]);


  // 카테고리 입력 처리 (디바운싱 적용)
  const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCategoryName(value);

    // 이전 타이머 클리어
    if (categoryTimeoutRef.current) {
      clearTimeout(categoryTimeoutRef.current);
    }

    // 새로운 타이머 설정 (300ms 디바운싱)
    categoryTimeoutRef.current = setTimeout(() => {
      handleCategorySearch(value);
    }, 300);
  };

  // 카테고리 검색 처리
  const handleCategorySearch = useCallback(async (value: string) => {
    if (value.trim().length > 0) {
      const result = await searchCategories(value);
      setCategoryList(result);
      setShowCategoryList(result.length > 0);
    } else {
      setCategoryList([]);
      setShowCategoryList(false);
    }
  }, [searchCategories]);

  // 카테고리 선택 처리
  const handleCategorySelect = (category: categories) => {
    setCategoryName(category.categoryName);
    setShowCategoryList(false);
    setCategoryList([]);
  };

  return (
    <>
      {/* 헤더 */}
      <div className="!flex !items-center !gap-3 !mb-4 !pt-4">
        <button 
          className="!flex !items-center !justify-center !w-8 !h-8 !rounded-full !bg-white !shadow-sm hover:!bg-gray-50 !transition-colors !border-0"
          onClick={() => navigate('/product')}
          type="button"
        >
          <svg className="!w-4 !h-4 !text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="!text-xl !font-bold !text-gray-900 !m-0">상품 등록</h1>
      </div>
      
      <div className="!grid !grid-cols-1 lg:!grid-cols-12 !gap-6">
        {/* 왼쪽: 정보 입력 */}
        <div className="lg:!col-span-8 !space-y-4">
          {/* 기본 정보와 가격 정보를 2단 그리드로 배치 */}
          <div className="!grid !grid-cols-1 md:!grid-cols-2 !gap-4">
            {/* 기본 정보 섹션 */}
            <div className="!bg-white !rounded-lg !p-5">
              <h2 className="!text-base !font-semibold !text-gray-900 !mb-4">기본 정보</h2>
              
              <div className="!space-y-4">
                <div className="!space-y-1">
                  <label className="!block !text-xs !font-medium !text-gray-700">상품 코드 *</label>
                  <PCode pRef={pCodeRef} />
                </div>
                <div className="!space-y-1">
                  <label className="!block !text-xs !font-medium !text-gray-700">서비스 형태 *</label>
                  <PService pRef={pServiceRef} />
                </div>
                <div className="!space-y-1">
                  <label className="!block !text-xs !font-medium !text-gray-700">상품명 *</label>
                  <PName pRef={pNameRef} value={pName} onChange={setPName} />
                </div>

                <div className="!space-y-1 category-input-wrapper">
                  <label className="!block !text-xs !font-medium !text-gray-700">카테고리 명</label>
                  <div className="!relative">
                    <Input
                        value={categoryName}
                        onChange={handleCategoryChange}
                        textAlign="left"
                        placeholder="카테고리를 입력하세요"
                        style={{ width: '100%' }}
                    />
                    {showCategoryList && categoryList.length > 0 && (
                        <div className="!absolute !z-10 !w-full !mt-1 !bg-white !border !border-gray-300 !rounded-lg !shadow-lg !max-h-48 !overflow-y-auto">
                          {categoryList.map((category) => (
                              <div
                                  key={category.id}
                                  className="!px-3 !py-2 !cursor-pointer hover:!bg-gray-100 !text-sm !transition-colors"
                                  onClick={() => handleCategorySelect(category)}
                              >
                                {category.categoryName}
                              </div>
                          ))}
                        </div>
                    )}
                  </div>
                </div>
                <div className="!space-y-1">
                  <label className="!block !text-xs !font-medium !text-gray-700">상품 샘플 (PDF)</label>
                  {existingSampleFile && !newSampleFile ? (
                      <div className="!flex !items-center !gap-2">
                        <a
                            href={existingSampleFile}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="!flex-1 !px-3 !py-2 !bg-gray-100 !rounded-lg !text-sm !text-blue-600 hover:!bg-gray-200 !transition-colors"
                        >
                          기존 샘플 파일 보기
                        </a>
                        <button
                            type="button"
                            onClick={() => setExistingSampleFile('')}
                            className="!px-3 !py-2 !bg-blue-500 !text-white !text-sm !rounded-lg hover:!bg-blue-600 !transition-colors"
                        >
                          변경
                        </button>
                      </div>
                  ) : newSampleFile ? (
                      <div className="!flex !items-center !gap-2">
                        <div className="!flex-1 !px-3 !py-2 !bg-green-50 !rounded-lg !text-sm !text-green-700">
                          {newSampleFile.file.name}
                        </div>
                        <button
                            type="button"
                            onClick={() => setNewSampleFile(null)}
                            className="!px-3 !py-2 !bg-red-500 !text-white !text-sm !rounded-lg hover:!bg-red-600 !transition-colors"
                        >
                          삭제
                        </button>
                      </div>
                  ) : (
                      <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => {
                            if (e.target.files && e.target.files.length > 0) {
                              setSampleFiles(Array.from(e.target.files));
                            }
                          }}
                          className="!w-full !px-3 !py-2 !border !border-gray-300 !rounded-lg !text-sm !bg-white !cursor-pointer file:!mr-3 file:!py-1 file:!px-3 file:!rounded file:!border-0 file:!text-sm file:!font-medium file:!bg-blue-50 file:!text-blue-700 hover:file:!bg-blue-100"
                      />
                  )}
                </div>
              </div>
            </div>
            
            {/* 가격 정보 섹션 */}
            <div className="!bg-white !rounded-lg !p-5">
              <h2 className="!text-base !font-semibold !text-gray-900 !mb-4">가격 정보</h2>
              
              <div className="!space-y-4">
                <div className="!space-y-1">
                  <PCost pRef={pCostRef} pCost={pCost} setPCost={setPCost} />
                </div>
                <div className="!space-y-1">
                  <PDiscount pRef={pDiscountRef} pCost={pCost} value={pDiscount} onChange={setPDiscount} />
                </div>
              </div>
            </div>
          </div>
          
          {/* 상품 설명 섹션 - 전체 너비 사용 */}
          <div className="!bg-white !rounded-lg !p-5">
            <h2 className="!text-base !font-semibold !text-gray-900 !mb-4">상품 설명</h2>
            <div className="!space-y-1">
              <label className="!block !text-xs !font-medium !text-gray-700">상품 설명 *</label>
              <PDescription pRef={pDescriptionRef} />
            </div>
          </div>
          
          {/* 이미지 섹션 */}
          <div className="!bg-white !rounded-lg !p-5">
            <h2 className="!text-base !font-semibold !text-gray-900 !mb-4">상품 이미지</h2>
            
            <div className="!grid !grid-cols-1 md:!grid-cols-3 !gap-4">
              <div className="!space-y-1">
                <label className="!block !text-xs !font-medium !text-gray-700">대표 이미지 *</label>
                <PThumbnail id={pThumbnailId} setId={setPThumbnailId} />
              </div>
              <div className="!space-y-1">
                <label className="!block !text-xs !font-medium !text-gray-700">속지 이미지 *</label>
                <PInlineImage id={pInlineId} setId={setPInlineId} />
              </div>
              <div className="!space-y-1">
                <label className="!block !text-xs !font-medium !text-gray-700">맺음말 이미지</label>
                <PInlineImage id={pEndFileId} setId={setPEndFileId} />
              </div>
              <div className="!space-y-1">
                <label className="!block !text-xs !font-medium !text-gray-700">상세 이미지 *</label>
                <PImage ids={pImagesId} setIds={setPImagesId} />
              </div>
            </div>
          </div>
        </div>
        
        {/* 오른쪽: 미리보기 */}
        <div className="lg:!col-span-4">
          <div className="!sticky !top-0">
            <div className="!bg-white !rounded-lg !p-5">
              <h3 className="!text-base !font-semibold !text-gray-900 !mb-4">미리보기</h3>
              
              {/* 미리보기 영역 - 점선 테두리로 구분 */}
              <div className="!border-2 !border-dashed !border-gray-300 !rounded-lg !p-4 !bg-gray-50/50">
                <div className="!mx-auto" style={{width: '252px'}}>
                  <div className="!bg-white !rounded-lg !overflow-hidden !shadow-md !border !border-gray-200 hover:!shadow-lg !transition-shadow !cursor-pointer">
                  {pThumbnailId ? (
                    <div className="!w-full !overflow-hidden" style={{aspectRatio: '252/363'}}>
                      <img 
                        src={URL.createObjectURL(pThumbnailId.file)} 
                        alt="preview"
                        className="!w-full !h-full !object-cover"
                      />
                    </div>
                  ) : (
                    <div className="!w-full !bg-gray-100 !flex !items-center !justify-center !text-gray-400 !text-sm" style={{aspectRatio: '252/363'}}>
                      이미지 미리보기
                    </div>
                  )}
                  
                  <div className="!p-4">
                    <div className="!font-normal !text-gray-800 !text-[15px] !leading-[22px] !mb-2 !min-h-[44px] !line-clamp-2">
                      {pName || '상품명을 입력하세요'}
                    </div>
                    <div className="!flex !items-center !gap-2 !flex-wrap">
                      {pDiscount && (
                        <>
                          <div className="!text-sm !text-gray-400 !line-through">
                            {pCost ? `${pCost}원` : '0원'}
                          </div>
                          <div className="!text-sm !font-semibold !text-red-500">
                            {Math.round(((parseInt(pCost.replace(/,/g, '')) - parseInt(pDiscount.replace(/,/g, ''))) / parseInt(pCost.replace(/,/g, ''))) * 100)}%
                          </div>
                        </>
                      )}
                      <div className="!text-lg !font-bold !text-gray-900">
                        {pDiscount || pCost ? `${pDiscount || pCost}원` : '0원'}
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 등록하기 버튼 */}
            <div className="!mt-4">
              <BigButton type="submit" text="상품 등록하기" onClick={onClickSubmit} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductCreate;
